import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';
import useBreakpoint from '../../hooks/useBreakpoint';

// ── Phone messages ────────────────────────────────────────────────────────────
// On a phone the messages behave the way a native app's do:
//
// - Toasts are a slim snackbar at the bottom, above the bottom nav, where they
//   cover neither the app bar nor the page title.
// - An error raised while a sheet / dialog / full-screen editor is open is
//   shown INSIDE that sheet, right above its buttons. A toast on top of a form
//   covers the very fields the message is about, and the operator has to close
//   it before they can fix anything.
//
// `toast.error` / `toast.warning` are patched once, so the 150-odd call sites
// keep calling sonner as they always have (same idea as utils/nativeFiles.js).
// Desktop and tablet are untouched: the patch passes straight through there.

const PHONE_QUERY = 'screen and (max-width: 767.98px)';
const EVT = 'm3-inline-message';
const CLEAR_EVT = 'm3-inline-message-clear';

const isPhone = () => typeof window !== 'undefined' && window.matchMedia?.(PHONE_QUERY).matches;

// Radix dialogs, vaul drawers (MSheet) and MScreen all carry role=dialog. A
// popover is also role=dialog but is a picker, not a form — never a target.
function topOpenDialog() {
  const all = Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]'));
  const open = all.filter((el) => (
    el.getAttribute('data-state') !== 'closed'
    && !el.closest('[data-radix-popper-content-wrapper]')
    && el.getClientRects().length > 0
  ));
  return open.length ? open[open.length - 1] : null; // portals append: last is on top
}

let installed = false;
function installPatch() {
  if (installed) return;
  installed = true;
  const route = (kind, original) => (message, opts) => {
    if (isPhone() && topOpenDialog()) {
      // Decide a moment later: a handler that warns and then closes its sheet
      // ("saved, but …" + onClose) must not have the message vanish with it.
      const id = `inline-${Date.now()}`;
      setTimeout(() => {
        if (topOpenDialog()) {
          window.dispatchEvent(new CustomEvent(EVT, {
            detail: { kind, message, description: opts?.description },
          }));
        } else {
          original(message, opts);
        }
      }, 80);
      return id;
    }
    return original(message, opts);
  };
  const clearThen = (original) => (message, opts) => {
    window.dispatchEvent(new Event(CLEAR_EVT));
    return original(message, opts);
  };
  toast.error = route('error', toast.error.bind(toast));
  toast.warning = route('warning', toast.warning.bind(toast));
  toast.success = clearThen(toast.success.bind(toast));
}

function InlineDialogMessage() {
  const [msg, setMsg] = useState(null); // { kind, message, description, host }
  const hostRef = useRef(null);
  const clear = useCallback(() => {
    if (hostRef.current?.isConnected) hostRef.current.remove();
    hostRef.current = null;
    setMsg(null);
  }, []);

  useEffect(() => {
    const onMsg = (e) => {
      const dialog = topOpenDialog();
      if (!dialog) return;
      let host = dialog.querySelector(':scope [data-m3-inline-msg]');
      if (!host) {
        host = document.createElement('div');
        host.setAttribute('data-m3-inline-msg', '');
        host.className = 'shrink-0';
        const footers = dialog.querySelectorAll('[data-dialog-footer], [data-m3-footer], [data-m3-screen-footer]');
        const footer = footers[footers.length - 1];
        if (footer?.hasAttribute('data-m3-screen-footer')) host.className = 'shrink-0 bg-white px-4';
        if (footer?.parentNode) footer.parentNode.insertBefore(host, footer);
        else dialog.appendChild(host);
      }
      if (hostRef.current && hostRef.current !== host && hostRef.current.isConnected) hostRef.current.remove();
      hostRef.current = host;
      setMsg({ ...e.detail, host });
      try { navigator.vibrate?.(40); } catch { /* not supported */ }
      requestAnimationFrame(() => host.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    };
    const onClear = () => clear();
    window.addEventListener(EVT, onMsg);
    window.addEventListener(CLEAR_EVT, onClear);
    return () => {
      window.removeEventListener(EVT, onMsg);
      window.removeEventListener(CLEAR_EVT, onClear);
    };
  }, [clear]);

  // The sheet closed (or re-rendered its body away): drop the message with it.
  useEffect(() => {
    if (!msg) return undefined;
    const t = setInterval(() => {
      if (!msg.host.isConnected) clear();
    }, 400);
    return () => clearInterval(t);
  }, [msg, clear]);

  if (!msg || !msg.host.isConnected) return null;
  const warn = msg.kind === 'warning';
  const Icon = warn ? AlertTriangle : AlertCircle;
  return createPortal(
    <div
      role="alert"
      className={`my-2 flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-[14px] leading-snug ${
        warn ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-red-300 bg-red-50 text-red-900'
      }`}
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${warn ? 'text-amber-600' : 'text-red-600'}`} />
      <div className="min-w-0 flex-1 break-words">
        <div className="font-medium">{msg.message}</div>
        {msg.description && <div className="mt-0.5 text-[13px] opacity-80">{msg.description}</div>}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={clear}
        className="-mr-1 -mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full active:bg-black/5"
      >
        <X className="h-4 w-4" />
      </button>
    </div>,
    msg.host,
  );
}

/** The app's one toaster: top-right on desktop, a bottom snackbar on a phone. */
export default function AppToaster() {
  const { isPhone: phone } = useBreakpoint();
  useEffect(() => { installPatch(); }, []);
  return (
    <>
      {phone ? (
        <Toaster
          position="bottom-center"
          duration={4000}
          closeButton={false}
          visibleToasts={2}
          swipeDirections={['left', 'right', 'bottom']}
          // Above the bottom nav (and the home indicator). Full-screen editors hide
          // the nav, and their errors go inline anyway (above).
          mobileOffset={{
            bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
            left: '0.75rem',
            right: '0.75rem',
          }}
        />
      ) : (
        // 7s, with a close button: several toasts here are full sentences naming
        // kilograms and a screen to go to, and 3s is not long enough to read one.
        <Toaster position="top-right" duration={7000} closeButton expand visibleToasts={4} />
      )}
      <InlineDialogMessage />
    </>
  );
}
